import { z } from "zod";
import { WordBoundarySchema } from "./media";

/**
 * Slide element types
 */
export const ElementTypeEnum = z.enum([
  "image",
  "video",
  "text",
  "audioPlayer",
  "recordButton",
  "shape",
  "line",
  "textChoice",
  "imageChoice",
  "dragMatchingText",
  "drahMatchingImage",
  "oddImageOut",
  "oddTextOut",
  "phonic",
  "answerImage",
  "orderImage",
  "orderText",
  "spelling",
  "speaking",
  "composition",
]);

/**
 * Line style types
 */
export const LineStyleEnum = z.enum(["solid", "dashed", "dotted", "wavy"]);

/**
 * Media schema for image , audio , video
 */
const MediaSchema = z.object({
  src: z.string().nullable(),
  id: z.string().nullable(),
  filename: z.string().nullable(),
  displaySrc: z.string().nullable().optional(),
});

/**
 * Base element schema for draggable/editable slide elements
 */
export const BaseElementSchema = z.object({
  id: z.string(),
  type: ElementTypeEnum,
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
  size: z.object({
    width: z.number(),
    height: z.number(),
  }),
  zIndex: z.number(),
  rotation: z.number().optional(),
  border: z.object({
    lineStyle: z.string().default("none"),
    strokeWidth: z.number().default(3),
    stroke: z.string().default("#000000"),
    cornerRadius: z.number().default(0),
  }),
  backgroundColor: z.string().nullable().default(null),
});

/**
 * Shape types
 */
export const ShapeTypeEnum = z.enum([
  "rectangle",
  "circle",
  "triangle",
  "diamond",
  "hexagon",
  "star",
  "heart",
  "cross",
  "sun",
  "arrow",
  "double-arrow",
  "tunnel",
]);

/**
 * Image element schema
 */
export const ImageElementSchema = BaseElementSchema.extend({
  type: z.literal("image"),
  media: MediaSchema,
  objectFit: z.enum(["cover", "contain", "fill"]).optional(),
  style: z
    .object({
      borderRadius: z.number().optional(),
      opacity: z.number().optional(),
    })
    .optional(),
});

/**
 * Video element schema
 */
export const VideoElementSchema = BaseElementSchema.extend({
  type: z.literal("video"),
  media: MediaSchema,
  thumbnail: z.string().optional(),
  autoPlay: z.boolean(),
  loop: z.boolean(),
  objectFit: z.enum(["cover", "contain", "fill"]).optional(),
  style: z
    .object({
      borderRadius: z.number().optional(),
      opacity: z.number().optional(),
    })
    .optional(),
});

/**
 * Text element schema
 */
export const TextElementSchema = BaseElementSchema.extend({
  type: z.literal("text"),
  content: z.string(),
  // Optional TTS audio (S3-backed) generated from `content`, played in the editor/live view.
  audio: MediaSchema.optional(),
  // How the element renders once it has audio: "text" shows the text (with playback highlight),
  // "player" shows only a play button + progress bar. Defaults to "text" when absent.
  audioDisplayMode: z.enum(["text", "player"]).optional(),
  // Per-word timings (ms) for highlighting text in sync with `audio` playback.
  audioWordBoundaries: z.array(WordBoundarySchema).optional(),
  style: z.object({
    fontSize: z.number(),
    fontWeight: z.string(),
    color: z.string(),
    fontFamily: z.string(),
    textAlign: z.string(),
    fontStyle: z.string(),
    textDecoration: z.string(),
  }),
});

/**
 * Audio player element schema

 */
/**
 * `script` is the narration `media` was synthesized from, kept so the author can read, edit and
 * speak it again. Optional: audio uploaded from the media library has no script behind it.
 */
export const AudioPlayerElementSchema = BaseElementSchema.extend({
  type: z.literal("audioPlayer"),
  media: MediaSchema,
  autoPlay: z.boolean(),
  loop: z.boolean(),
  controls: z.boolean().optional(),
  script: z.string().optional(),
});

/**
 * Phonic element schema

 */
export const PhonicElementSchema = BaseElementSchema.extend({
  type: z.literal("phonic"),
  media: MediaSchema,
  autoPlay: z.boolean(),
  loop: z.boolean(),
  controls: z.boolean().optional(),
  phoneme: z.string(),
  phonemeType: z.string(),
});

/**
 * Record button element schema
 */
export const RecordButtonElementSchema = BaseElementSchema.extend({
  type: z.literal("recordButton"),
  label: z.string().optional(),
  maxDuration: z.number().optional(),
  style: z
    .object({
      backgroundColor: z.string().optional(),
      textColor: z.string().optional(),
      borderRadius: z.number().optional(),
    })
    .optional(),
});

/**
 * Shape element schema
 */
export const ShapeElementSchema = BaseElementSchema.extend({
  type: z.literal("shape"),
  shapeType: ShapeTypeEnum,
  fill: z.string().optional(),
  stroke: z.string().optional(),
  strokeWidth: z.number().optional(),
});

/**
 * Line arrow types (for endpoints)
 */
export const ArrowTypeEnum = z.enum(["none", "arrow", "circle", "square", "diamond"]);

/**
 * Shape element schema
 */
export const LineElementSchema = BaseElementSchema.extend({
  type: z.literal("line"),

  // Endpoint coordinates (absolute positions on canvas)
  startX: z.number(),
  startY: z.number(),
  endX: z.number(),
  endY: z.number(),

  // Styling
  stroke: z.string().default("#000000"),
  strokeWidth: z.number().min(1).max(100).default(2),
  lineStyle: LineStyleEnum.default("solid"),

  // Arrow endpoints (optional)
  startArrow: ArrowTypeEnum.default("none").optional(),
  endArrow: ArrowTypeEnum.default("none").optional(),

  // Optional styling
  opacity: z.number().min(0).max(1).default(1).optional(),
  dashArray: z.string().optional(),
});

/**
 * Text choice element schema (for multiple choice questions)
 */
export const TextChoiceElementSchema = BaseElementSchema.extend({
  type: z.literal("textChoice"),
  isCorrectAnswer: z.boolean(),
  text: z.string(),
  style: z.object({
    fontSize: z.number(),
    fontWeight: z.string(),
    color: z.string(),
    fontFamily: z.string(),
    textAlign: z.string(),
    fontStyle: z.string(),
    textDecoration: z.string(),
  }),
});

/**
 * Image choice element schema (for multiple choice questions with images)
 */
export const ImageChoiceElementSchema = BaseElementSchema.extend({
  type: z.literal("imageChoice"),
  isCorrectAnswer: z.boolean(),
  media: MediaSchema,
});

/**
 * Drag matching text to image element schema
 */
export const DragMatchingTextElementSchema = BaseElementSchema.extend({
  type: z.literal("dragMatchingText"),
  textChoices: z.array(z.object({ text: z.string(), isCorrectAnswer: z.boolean() })),
  media: MediaSchema,
});

/**
 * Drag matching image to text element schema
 */
export const DragMatchingImageElementSchema = BaseElementSchema.extend({
  type: z.literal("dragMatchingImage"),
  imageChoices: z.array(z.object({ media: MediaSchema, isCorrectAnswer: z.boolean() })),
  text: z.string(),
});

/**
 * Odd Text Out element schema
 */
export const OddTextOutElementSchema = BaseElementSchema.extend({
  type: z.literal("oddTextOut"),
  textChoices: z.array(z.object({ text: z.string(), isCorrectAnswer: z.boolean() })),
});

/**
 * Odd Image Out element schema
 */
export const OddImageOutElementSchema = BaseElementSchema.extend({
  type: z.literal("oddImageOut"),
  imageChoices: z.array(z.object({ media: MediaSchema, isCorrectAnswer: z.boolean() })),
});

/**
 * Fill in the blanks schema
 */
export const FillBlanksElementSchema = BaseElementSchema.extend({
  type: z.literal("fillBlanks"),
  text: z.string(),
  isTextOnly: z.boolean(),
  answerMode: z.enum(["choice", "type"]).default("choice"),
  // Optional manual sentence font size (px). When unset, the renderer scales it to the card.
  fontSize: z.number().optional(),
  blocks: z.array(
    z.object({
      media: MediaSchema.optional(), // optional media for this blank
      options: z.array(
        z.object({
          text: z.string(), // option text
          isCorrectAnswer: z.boolean(),
        })
      ),
    })
  ),
});

/**
 * Question and answer (image) element schema
 */
export const AnswerImageElementSchema = BaseElementSchema.extend({
  type: z.literal("answerImage"),
  media: MediaSchema,
  questionText: z.string(),
  correctAnswer: z.string(),
});

/**
 * Order image element schema (image ordering question)
 */
export const OrderImageElementSchema = BaseElementSchema.extend({
  type: z.literal("orderImage"),
  mediaType: z.enum(["audio", "video"]).nullable().default(null),
  media: MediaSchema.nullable().default(null),
  // Tiles per row. Rows follow from the item count, so there is nothing to author for them.
  // Optional so existing elements keep parsing; `clampColumns` in canvas-styles defaults it.
  columns: z.number().int().optional(),
  // Teacher multiplier on the auto-fitted tile size; above 1 the grid outgrows the frame and clips.
  // A ratio, not pixels: every size in this element is frame-relative so the web editor and the
  // phone lay the grid out identically at any resolution.
  imageScale: z.number().optional(),
  items: z.array(
    z.object({
      id: z.string(),
      media: MediaSchema,
      // Both retained because they are required and every existing element carries them; the grid
      // derives its layout from `columns`/`imageScale` and no renderer reads either field.
      position: z.object({ x: z.number(), y: z.number() }),
      size: z.object({ width: z.number(), height: z.number() }),
      order: z.number().optional(),
    })
  ),
});

/**
 * Order text element schema (text ordering question)
 */
export const OrderTextElementSchema = BaseElementSchema.extend({
  type: z.literal("orderText"),
  mediaType: z.enum(["audio", "video"]).nullable().default(null),
  media: MediaSchema.nullable().default(null),
  items: z.array(
    z.object({
      id: z.string(),
      text: z.string(),
    })
  ),
  // Item row text size (px); undefined falls back to the renderers' own default.
  fontSize: z.number().optional(),
});

/**
 * Spelling element schema (letter-by-letter spelling question)
 * Box count is derived from `word.length` at render time — not stored separately.
 * Revealed letters are pre-filled/locked and excluded from both validation and scoring; read
 * them through `getRevealedIndexes` in canvas-core rather than off either field below.
 */
export const SpellingElementSchema = BaseElementSchema.extend({
  type: z.literal("spelling"),
  word: z.string(),
  // Superseded by `revealedIndexes`, kept because it is required and every existing element —
  // including everything the API's PDF import produces — carries it. Writers set both (see
  // `buildRevealProps`); readers must not test it directly, or they disagree with any element
  // that reveals something other than the first letter.
  revealFirstLetter: z.boolean(),
  // Which letter boxes are pre-filled and locked, as indexes into `word`. Replaces the boolean
  // above, which could only ever reveal index 0: teaching spelling means choosing *which* letters
  // to leave out (hide the vowels, hide the ending), and "how many" alone can't express that.
  // Optional so existing elements keep parsing; `getRevealedIndexes` falls back to the boolean
  // and drops indexes left stranded past the end of a shortened word.
  revealedIndexes: z.array(z.number().int().nonnegative()).optional(),
  // Teacher-adjustable multiplier on the frame-derived letter box size, applied in the shared
  // `getSpellingStyles`. Exists because auto-fitting a long word into the frame's width shrinks
  // the boxes until the letters are too small to read on a phone; the row can scroll horizontally
  // now, so the teacher can pin a legible box size and let a long word run off the edge instead.
  //
  // Deliberately a ratio, not an absolute pixel size, for the same reason as the speaking
  // element's font scales above: every size here is frame-relative so web and mobile render
  // identically at any resolution, and an absolute px field would reintroduce that divergence.
  // Optional so existing elements keep parsing; the style function defaults and clamps it, so an
  // absent, zero, or out-of-range value can never collapse the boxes or overflow the frame.
  boxScale: z.number().optional(),
});

/**
 * Speaking element schema (sentence-by-sentence listening comprehension question)
 * Each sentence owns its own TTS audio (generated via the `synthesizeSpeech` mutation, not
 * teacher-uploaded) and exactly four options, one marked correct. Modeled on
 * `FillBlanksElementSchema`'s `blocks[].options[]` shape - an array of sentence objects, each
 * owning its options array - per the same convention.
 */
export const SpeakingElementSchema = BaseElementSchema.extend({
  type: z.literal("speaking"),
  sentences: z.array(
    z.object({
      text: z.string(),
      audio: MediaSchema.optional(),
      options: z.array(
        z.object({
          text: z.string(),
          isCorrectAnswer: z.boolean(),
        })
      ),
    })
  ),
  // Teacher-adjustable multipliers applied, in the shared `getSpeakingStyles` function, to the
  // sentence text size and option text size independently. Deliberately ratios, not absolute
  // pixel sizes: every size in this element is frame-relative so web and mobile render
  // identically at any resolution; an absolute px field would reintroduce exactly the web/mobile
  // divergence the spelling element shipped with and had to fix. Both optional so existing
  // elements keep parsing; the style function defaults and clamps each independently, so an
  // absent, zero, or out-of-range value can never collapse the text or overflow the frame.
  //
  // Replaces the earlier single `fontScale` field (removed, not renamed): this branch was never
  // merged to main, so there is no persisted production data carrying the old field to migrate.
  // A test slide authored against the old single-field shape simply falls back to the default
  // scale for both sentence and option text — Zod strips the now-unrecognized `fontScale` key
  // during parsing, and the style function treats the (now-absent) new fields the same as any
  // other absent value, so this never crashes or produces NaN, it just loses the old setting.
  sentenceFontScale: z.number().optional(),
  optionFontScale: z.number().optional(),
});

/**
 * Composition element: the student photographs their handwritten composition
 * and AI corrects it (see docs/plans/2026-08-28-composition-question-element.md).
 * `prompt` is the writing task shown to the student.
 *
 * Deliberately NOT in `questionElementSchemas`: corrections must not trigger
 * auto-advance or feed slide scoring — results live in the API's
 * compositionCorrections records, queried per (slideId, elementId, student).
 */
export const CompositionElementSchema = BaseElementSchema.extend({
  type: z.literal("composition"),
  prompt: z.string(),
});

/**
 * Union schema for all slide elements
 */
export const SlideElementSchema = z.discriminatedUnion("type", [
  ImageElementSchema,
  VideoElementSchema,
  TextElementSchema,
  AudioPlayerElementSchema,
  RecordButtonElementSchema,
  ShapeElementSchema,
  LineElementSchema,
  TextChoiceElementSchema,
  ImageChoiceElementSchema,
  DragMatchingTextElementSchema,
  DragMatchingImageElementSchema,
  OddImageOutElementSchema,
  OddTextOutElementSchema,
  FillBlanksElementSchema,
  AnswerImageElementSchema,
  PhonicElementSchema,
  OrderImageElementSchema,
  OrderTextElementSchema,
  SpellingElementSchema,
  SpeakingElementSchema,
  CompositionElementSchema,
]);

// Infer types from schemas
export type ElementType = z.infer<typeof ElementTypeEnum>;
export type BaseElement = z.infer<typeof BaseElementSchema>;
export type ShapeType = z.infer<typeof ShapeTypeEnum>;
export type ImageElement = z.infer<typeof ImageElementSchema>;
export type VideoElement = z.infer<typeof VideoElementSchema>;
export type TextElement = z.infer<typeof TextElementSchema>;
export type AudioPlayerElement = z.infer<typeof AudioPlayerElementSchema>;
export type RecordButtonElement = z.infer<typeof RecordButtonElementSchema>;
export type ShapeElement = z.infer<typeof ShapeElementSchema>;
export type LineStyle = z.infer<typeof LineStyleEnum>;
export type ArrowType = z.infer<typeof ArrowTypeEnum>;
export type LineElement = z.infer<typeof LineElementSchema>;
export type SlideElement = z.infer<typeof SlideElementSchema>;
export type TextChoiceElement = z.infer<typeof TextChoiceElementSchema>;
export type ImageChoiceElement = z.infer<typeof ImageChoiceElementSchema>;
export type DragMatchingTextElement = z.infer<typeof DragMatchingTextElementSchema>;
export type DragMatchingImageElement = z.infer<typeof DragMatchingImageElementSchema>;
export type OddImageOutElement = z.infer<typeof OddImageOutElementSchema>;
export type OddTextOutElement = z.infer<typeof OddTextOutElementSchema>;
export type FillBlanksElement = z.infer<typeof FillBlanksElementSchema>;
export type AnswerImageElement = z.infer<typeof AnswerImageElementSchema>;
export type PhonicElement = z.infer<typeof PhonicElementSchema>;
export type OrderImageElement = z.infer<typeof OrderImageElementSchema>;
export type OrderTextElement = z.infer<typeof OrderTextElementSchema>;
export type SpellingElement = z.infer<typeof SpellingElementSchema>;
export type SpeakingElement = z.infer<typeof SpeakingElementSchema>;
export type CompositionElement = z.infer<typeof CompositionElementSchema>;

const questionElementSchemas = [
  TextChoiceElementSchema,
  ImageChoiceElementSchema,
  DragMatchingTextElementSchema,
  DragMatchingImageElementSchema,
  OddImageOutElementSchema,
  OddTextOutElementSchema,
  FillBlanksElementSchema,
  OrderImageElementSchema,
  OrderTextElementSchema,
  SpellingElementSchema,
  SpeakingElementSchema,
] as const;

export const QuestionTypeEnum = z.enum(
  questionElementSchemas.map((s) => s.shape.type.value) as [string, ...string[]]
);
